import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, MapPin, GraduationCap, ChatCircleText } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

// Shared alumni card with View / Connect / Message — used by Directory + AI Search.
export default function AlumniResultCard({ p, index = 0 }) {
  const nav = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const inst = p.education?.[0]?.institution;

  const connect = async () => {
    setConnecting(true);
    try {
      await api.post("/connections", { addressee_id: p.user_id });
      toast.success("Connection request sent");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast[e.response?.status === 400 ? "message" : "error"](detail || "Could not send request. Please try again.");
    } finally { setConnecting(false); }
  };
  const message = async () => {
    try {
      const { data } = await api.post("/dm/start", { user_id: p.user_id });
      nav(`/messages?c=${data.id}`);
    } catch { toast.error("Could not open chat"); }
  };

  return (
    <Card className="group border-border/60 p-6 shadow-sm transition-colors hover:border-secondary/40 animate-fade-up" style={{ animationDelay: `${index * 0.03}s` }} data-testid="alumni-card">
      <div className="flex items-start gap-4">
        <Avatar className="h-14 w-14"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <Link to={`/u/${p.public_slug || p.user_id}`} className="block truncate font-heading text-lg font-medium hover:underline">{p.name}</Link>
          <p className="truncate text-sm text-muted-foreground">{p.headline || "Alumnus"}</p>
          {p.location && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={13} /> {p.location}</p>}
        </div>
      </div>
      {inst && <Badge variant="secondary" className="mt-4 gap-1 rounded-full font-normal"><GraduationCap size={13} /> {inst}</Badge>}
      <div className="mt-5 flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 rounded-full gap-1.5" onClick={message} data-testid="alumni-message"><ChatCircleText size={15} /> Message</Button>
        <Button size="sm" className="flex-1 rounded-full gap-1.5" onClick={connect} disabled={connecting} data-testid="alumni-connect"><UserPlus size={15} /> Connect</Button>
      </div>
    </Card>
  );
}
